import { DateTime } from 'luxon';
import {
    columnFilteringFeature,
    columnOrderingFeature,
    columnPinningFeature,
    columnResizingFeature,
    columnSizingFeature,
    columnVisibilityFeature,
    createCoreRowModel,
    createPaginatedRowModel,
    createSortedRowModel,
    rowPaginationFeature,
    rowSortingFeature,
    tableFeatures,
    type ColumnDef,
    type RowData,
} from '@tanstack/react-table';
import type { AppColumn, AppScalarType, AppScalarValue, AppTableProps } from './AppTable.types';

export const appTableFeatures = tableFeatures({
    columnFilteringFeature,
    columnOrderingFeature,
    columnPinningFeature,
    columnResizingFeature,
    columnSizingFeature,
    columnVisibilityFeature,
    rowPaginationFeature,
    rowSortingFeature,
    coreRowModel: createCoreRowModel(),
    sortedRowModel: createSortedRowModel(),
    paginatedRowModel: createPaginatedRowModel(),
});

export type AppTableFeatureSet = typeof appTableFeatures;
export type AppTanStackColumn<TRow extends RowData> = ColumnDef<AppTableFeatureSet, TRow, unknown>;

interface AppColumnNode<TRow> {
    group?: { id: string; label: string; parentId?: string };
    column?: AppColumn<TRow>;
    children: AppColumnNode<TRow>[];
    order: number;
}

function toTanStackColumn<TRow extends RowData>(
    appColumn: AppColumn<TRow>,
    resizeEnabled: boolean,
): AppTanStackColumn<TRow> {
    return {
        id: appColumn.id,
        header: appColumn.headerLabel ?? appColumn.label,
        accessorFn: appColumn.accessor,
        enableSorting: appColumn.kind !== 'action' && appColumn.sortable !== false,
        sortDescFirst: false,
        enableColumnFilter: false,
        enableHiding: appColumn.kind !== 'action',
        enablePinning: appColumn.kind !== 'action',
        enableResizing: resizeEnabled && appColumn.kind !== 'action',
        minSize: appColumn.minWidth,
        size: appColumn.width ?? Math.max(appColumn.minWidth ?? 20, 150 * (appColumn.flex ?? 1)),
        sortFn: (rowA, rowB, columnId) =>
            compareAppValues(
                appColumn.sortAccessor
                    ? appColumn.sortAccessor(rowA.original)
                    : rowA.getValue<AppScalarValue>(columnId),
                appColumn.sortAccessor
                    ? appColumn.sortAccessor(rowB.original)
                    : rowB.getValue<AppScalarValue>(columnId),
                appColumn.sortScalarType ?? appColumn.scalarType,
            ),
    };
}

export function createAppColumnDefinitions<TRow extends RowData>(
    columns: readonly AppColumn<TRow>[],
    resizeEnabled: boolean,
): AppTanStackColumn<TRow>[] {
    const nodes: AppColumnNode<TRow>[] = [];
    const groupNodes = new Map<string, AppColumnNode<TRow>>();

    columns.forEach((column, index) => {
        const group = column.groupHeader;

        if (!group) {
            nodes.push({
                column,
                children: [],
                order: index,
            });

            return;
        }

        for (const groupPart of [group]) {
            const existing = groupNodes.get(groupPart.id);

            if (existing) {
                existing.order = Math.min(existing.order, index);
                continue;
            }
            groupNodes.set(groupPart.id, {
                group: groupPart,
                children: [],
                order: index,
            });
        }

        const groupNode = groupNodes.get(group.id);

        if (groupNode) {
            groupNode.children.push({
                column,
                children: [],
                order: index,
            });
            groupNode.order = Math.min(groupNode.order, index);
        }
    });

    for (const groupNode of groupNodes.values()) {
        const parentId = groupNode.group?.parentId;
        const parent = parentId ? groupNodes.get(parentId) : undefined;
        let ancestor = parent;
        let cyclic = false;

        while (ancestor) {
            if (ancestor === groupNode) {
                cyclic = true;
                break;
            }
            ancestor = ancestor.group?.parentId ? groupNodes.get(ancestor.group.parentId) : undefined;
        }

        if (parent && !cyclic) {
            parent.children.push(groupNode);
        } else {
            nodes.push(groupNode);
        }
    }
    nodes.sort((a, b) => a.order - b.order);

    const sortChildren = (node: AppColumnNode<TRow>) => {
        node.children.sort((a, b) => a.order - b.order);
        node.children.forEach(sortChildren);
    };

    nodes.forEach(sortChildren);

    const toDefinition = (node: AppColumnNode<TRow>): AppTanStackColumn<TRow> => {
        if (node.column) {
            return toTanStackColumn(node.column, resizeEnabled);
        }

        return {
            id: node.group?.id ?? `group-${node.order}`,
            header: node.group?.label ?? '',
            columns: node.children.map(toDefinition),
        };
    };

    return nodes.map(toDefinition);
}

export function compareAppValues(left: AppScalarValue, right: AppScalarValue, scalarType: AppScalarType): number {
    const leftEmpty = left === null || left === undefined || left === '';
    const rightEmpty = right === null || right === undefined || right === '';

    if (leftEmpty || rightEmpty) {
        return leftEmpty === rightEmpty ? 0 : leftEmpty ? 1 : -1;
    }

    if (scalarType === 'date' || scalarType === 'dateTime') {
        const leftDate =
            left instanceof Date
                ? DateTime.fromJSDate(left)
                : typeof left === 'string'
                  ? DateTime.fromISO(left, {
                        setZone: true,
                    })
                  : undefined;
        const rightDate =
            right instanceof Date
                ? DateTime.fromJSDate(right)
                : typeof right === 'string'
                  ? DateTime.fromISO(right, {
                        setZone: true,
                    })
                  : undefined;
        const leftValid = leftDate?.isValid === true;
        const rightValid = rightDate?.isValid === true;

        if (!leftValid || !rightValid) {
            return leftValid === rightValid ? 0 : leftValid ? -1 : 1;
        }

        if (!leftDate || !rightDate) {
            return 0;
        }

        if (scalarType === 'date') {
            return (leftDate.toISODate() ?? '').localeCompare(rightDate.toISODate() ?? '');
        }

        return leftDate.toMillis() - rightDate.toMillis();
    }

    if (scalarType === 'number') {
        const leftValid = typeof left === 'number' && Number.isFinite(left);
        const rightValid = typeof right === 'number' && Number.isFinite(right);

        if (!leftValid || !rightValid) {
            return leftValid === rightValid ? 0 : leftValid ? -1 : 1;
        }

        if (typeof left !== 'number' || typeof right !== 'number') {
            return 0;
        }

        return left - right;
    }

    if (scalarType === 'boolean' && typeof left === 'boolean' && typeof right === 'boolean') {
        return Number(left) - Number(right);
    }

    return String(left).localeCompare(String(right), undefined, {
        numeric: true,
        sensitivity: 'base',
    });
}

export function formatAppScalar(value: AppScalarValue, scalarType: AppScalarType): string {
    if (value === null || value === undefined) {
        return '';
    }

    if (value instanceof Date) {
        const date = DateTime.fromJSDate(value);

        return date.isValid ? date.toFormat(scalarType === 'date' ? 'dd/MM/yyyy' : 'dd/MM/yyyy HH:mm') : '';
    }

    if ((scalarType === 'date' || scalarType === 'dateTime') && typeof value === 'string') {
        const date = DateTime.fromISO(value, {
            setZone: true,
        });

        return date.isValid ? date.toFormat(scalarType === 'date' ? 'dd/MM/yyyy' : 'dd/MM/yyyy HH:mm') : value;
    }

    return String(value);
}

export function getInitialTableState<TRow>(props: Pick<AppTableProps<TRow>, 'pagination' | 'columns'>) {
    const initialState: {
        pagination?: { pageIndex: number; pageSize: number };
        columnSizing?: Record<string, number>;
    } = {};

    if (props.pagination) {
        initialState.pagination = {
            pageIndex: 0,
            pageSize: props.pagination.pageSize ?? 100,
        };
    }

    const sizing = Object.fromEntries(
        props.columns.filter((column) => column.width !== undefined).map((column) => [column.id, column.width!]),
    ) as Record<string, number>;

    if (Object.keys(sizing).length > 0) {
        initialState.columnSizing = sizing;
    }

    return initialState;
}
